import React, { useState, useEffect } from 'react';
import apiService from '../../services/apiService';
import toast from 'react-hot-toast';
import { Eye, TrendingUp, Activity, LayoutList, AlertCircle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import TaskCompareModal from './TaskCompareModal';

const OrganicSeoTasksManager = ({ domain }) => {
    const { user } = useAuth();
    const { isCreator } = usePermissions();
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(false);
    
    // Form state
    const [taskType, setTaskType] = useState('new_post');
    const [topic, setTopic] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    // Auto-pilot state
    const [isCrawling, setIsCrawling] = useState(false);
    
    // Tab and pagination state
    const [activeTab, setActiveTab] = useState('queue'); // 'queue' or 'completed'
    const [displayLimit, setDisplayLimit] = useState(20);
    
    // Modal state
    const [compareTask, setCompareTask] = useState(null);

    // Derived stats
    const totalProcessing = tasks.filter(t => ['pending', 'analyzing', 'writing', 'publishing'].includes(t.status)).length;
    const totalDone = tasks.filter(t => t.status === 'completed').length;
    const totalFailed = tasks.filter(t => t.status === 'failed').length;

    useEffect(() => {
        if (domain && domain.id) {
            fetchTasks();
            const interval = setInterval(fetchTasks, 15000); // refresh every 15s
            return () => clearInterval(interval);
        }
    }, [domain]);

    const fetchTasks = async () => {
        setLoading(true);
        try {
            const data = await apiService.makeRequest(`/organic-seo/tasks?domain_id=${domain.id}&limit=500`);
            setTasks(data || []);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateTask = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            await apiService.makeRequest(`/organic-seo/tasks?domain_id=${domain.id}&task_type=${taskType}&topic_or_source=${encodeURIComponent(topic)}`, { method: 'POST' });
            toast.success("Task created! AI agents are now working.");
            setTopic('');
            fetchTasks();
            setActiveTab('queue');
        } catch (error) {
            toast.error(error.message || "Failed to create task");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCrawlSite = async () => {
        setIsCrawling(true);
        try {
            const resp = await apiService.makeRequest(`/organic-seo/domains/${domain.id}/crawl`, { method: 'POST' });
            toast.success(resp.message || "Crawler started!");
            fetchTasks();
            setActiveTab('queue');
        } catch (error) {
            toast.error(error.message || "Failed to start crawler");
        } finally {
            setIsCrawling(false);
        }
    };
    const handleForceRun = async (taskId) => {
        try {
            await apiService.makeRequest(`/organic-seo/tasks/${taskId}/force-run`, { method: 'POST' });
            toast.success("Task is executing immediately!");
            fetchTasks();
        } catch (error) {
            toast.error(error.message || "Failed to force run task.");
        }
    };

    // Sort newest first
    const sortedTasks = [...tasks].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

    const filteredTasks = sortedTasks.filter(t => {
        if (activeTab === 'queue') {
            return ['pending', 'analyzing', 'writing'].includes(t.status);
        } else {
            return ['publishing', 'completed', 'failed'].includes(t.status);
        }
    });

    const paginatedTasks = filteredTasks.slice(0, displayLimit);
    const hasMore = paginatedTasks.length < filteredTasks.length;

    return (
        <div className="organic-seo-tasks mt-4">
            {/* Auto Pilot & UI Re-Designer Actions */}
            <div className="flex gap-4 mb-4">
                <div className="widget-card bg-indigo-50 border-indigo-200 shadow-sm flex-1">
                    <div className="flex justify-between items-center mb-2">
                        <div>
                            <h3 className="text-gray-900 font-bold">Auto-Pilot: Audit Entire Site</h3>
                            <p className="text-gray-600 text-sm">Automatically crawls all existing pages/posts on the domain and queues them for rewriting based on your Custom Prompt.</p>
                        </div>
                        <button onClick={handleCrawlSite} className="btn bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 px-4 rounded shadow-sm" disabled={isCrawling}>
                            {isCrawling ? 'Crawling...' : 'Start Auto-Pilot Crawler'}
                        </button>
                    </div>
                </div>

                <div className="widget-card bg-blue-50 border-blue-200 shadow-sm flex flex-col justify-center items-center" style={{ width: '250px' }}>
                    <h3 className="text-gray-900 font-bold text-center mb-2">Katifi Manus (Variant 1)</h3>
                    <button 
                        onClick={() => {
                            if (!isCreator && user?.email !== 'afitbk@gmail.com') {
                                toast.error('Только Создатель (Creator) имеет право на запуск интеллектуального агента Katifi Manus.');
                                return;
                            }
                            window.dispatchEvent(new CustomEvent('launchKatifiManus', { 
                                detail: { command: `Создай или полностью переделай дизайн главной страницы (Лендинг) для сайта ${domain?.url || 'этого сайта'}. Сохрани суть текстов, но используй современный Tailwind CSS, стильные градиенты, крутые секции и картинки из Unsplash.` }
                            }));
                        }}
                        className="btn bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded shadow-sm flex items-center gap-2 w-full justify-center"
                    >
                        <span>🧠 UI Re-Designer</span>
                    </button>
                </div>
            </div>

            {/* AI Metrics Dashboards (Inspired by Voice AI) */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
                <div style={{ padding: '1.25rem', borderRadius: '0.75rem', background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)', color: 'white', display: 'flex', flexDirection: 'column' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', opacity: 0.9 }}>
                        <h4 style={{ margin: 0, fontSize: '0.875rem', fontWeight: 500 }}>Total Tracked</h4>
                        <div style={{ backgroundColor: 'rgba(255,255,255,0.2)', padding: '0.5rem', borderRadius: '0.5rem' }}><Activity size={18} /></div>
                    </div>
                    <span style={{ fontSize: '2rem', fontWeight: 'bold', marginTop: '0.5rem' }}>{tasks.length}</span>
                </div>
                
                <div style={{ padding: '1.25rem', borderRadius: '0.75rem', background: 'linear-gradient(135deg, #059669 0%, #047857 100%)', color: 'white', display: 'flex', flexDirection: 'column' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', opacity: 0.9 }}>
                        <h4 style={{ margin: 0, fontSize: '0.875rem', fontWeight: 500 }}>Successfully Published</h4>
                        <div style={{ backgroundColor: 'rgba(255,255,255,0.2)', padding: '0.5rem', borderRadius: '0.5rem' }}><TrendingUp size={18} /></div>
                    </div>
                    <span style={{ fontSize: '2rem', fontWeight: 'bold', marginTop: '0.5rem' }}>{totalDone}</span>
                </div>

                <div style={{ padding: '1.25rem', borderRadius: '0.75rem', background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)', color: 'white', display: 'flex', flexDirection: 'column' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', opacity: 0.9 }}>
                        <h4 style={{ margin: 0, fontSize: '0.875rem', fontWeight: 500 }}>Active Drip Queue</h4>
                        <div style={{ backgroundColor: 'rgba(255,255,255,0.2)', padding: '0.5rem', borderRadius: '0.5rem' }}><LayoutList size={18} /></div>
                    </div>
                    <span style={{ fontSize: '2rem', fontWeight: 'bold', marginTop: '0.5rem' }}>{totalProcessing}</span>
                </div>

                <div style={{ padding: '1.25rem', borderRadius: '0.75rem', background: 'linear-gradient(135deg, #ea580c 0%, #c2410c 100%)', color: 'white', display: 'flex', flexDirection: 'column' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', opacity: 0.9 }}>
                        <h4 style={{ margin: 0, fontSize: '0.875rem', fontWeight: 500 }}>Failed Tasks</h4>
                        <div style={{ backgroundColor: 'rgba(255,255,255,0.2)', padding: '0.5rem', borderRadius: '0.5rem' }}><AlertCircle size={18} /></div>
                    </div>
                    <span style={{ fontSize: '2rem', fontWeight: 'bold', marginTop: '0.5rem' }}>{totalFailed}</span>
                </div>
            </div>

            <div className="widget-card">
                <div className="tasks-header">
                    <h3>Manual Content Production</h3>
                </div>

                <form onSubmit={handleCreateTask} className="task-creator-form">
                    <div className="form-group">
                        <label>Task Type</label>
                        <select value={taskType} onChange={e => setTaskType(e.target.value)}>
                            <option value="new_post">New Blog Post</option>
                            <option value="rewrite">Rewrite Existing HTML</option>
                            <option value="seo_audit">SEO Audit & Fix</option>
                        </select>
                    </div>
                    <div className="form-group">
                        <label>Topic / Source Text</label>
                        <textarea 
                            value={topic} 
                            onChange={e => setTopic(e.target.value)} 
                            placeholder={taskType === 'new_post' ? "e.g. How to set up an LLC in NY" : "Paste existing HTML here..."} 
                            rows="3"
                            required 
                        />
                    </div>
                    <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                        {isSubmitting ? "Submitting..." : "Generate Content"}
                    </button>
                </form>
            </div>

            <div className="widget-card mt-4 tasks-list shadow-sm">
                <div className="flex justify-between items-center mb-4 border-b border-gray-200 pb-2">
                    <h3>Rewritten Pages Tracker</h3>
                    <div className="flex gap-2">
                        <button 
                            onClick={() => { setActiveTab('queue'); setDisplayLimit(20); }}
                            className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${activeTab === 'queue' ? 'bg-blue-50 text-blue-700 border-t border-l border-r border-blue-200' : 'bg-transparent text-gray-500 hover:text-gray-700'}`}
                        >
                            Active Tasks ({totalProcessing})
                        </button>
                        <button 
                            onClick={() => { setActiveTab('completed'); setDisplayLimit(20); }}
                            className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${activeTab === 'completed' ? 'bg-green-50 text-green-700 border-t border-l border-r border-green-200' : 'bg-transparent text-gray-500 hover:text-gray-700'}`}
                        >
                            Completed / Live URLs
                        </button>
                    </div>
                </div>

                {loading && tasks.length === 0 ? <p>Loading tasks...</p> : (
                    <table className="tasks-table">
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Type</th>
                                <th>Topic / URL</th>
                                <th>Status</th>
                                <th style={{ textAlign: 'center' }}>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedTasks.map(t => (
                                <tr key={t.id}>
                                    <td className="text-gray-500 text-sm font-mono">{new Date(t.created_at).toLocaleDateString()}</td>
                                    <td><span className="badge font-medium text-xs bg-gray-100 text-gray-700 border border-gray-200">{t.task_type}</span></td>
                                    <td className="truncate max-w-xs text-gray-800 font-medium" title={t.live_url || t.topic_or_source}>{t.live_url || t.topic_or_source}</td>
                                    <td>
                                        <span className={`status-badge border ${t.status}`}>
                                            {t.status === 'writing' ? '🤖 Writing' : 
                                             t.status === 'publishing' ? '🚀 Publishing' :
                                             t.status === 'completed' ? '✅ Done' : t.status === 'failed' ? '❌ Failed' : t.status}
                                        </span>
                                    </td>
                                    <td style={{ textAlign: 'center' }}>
                                        <div className="flex gap-2 justify-end">
                                            {t.live_url && (
                                                <a href={t.live_url} target="_blank" rel="noopener noreferrer" className="px-3 py-1 flex items-center justify-center rounded-md transition-colors text-xs font-semibold bg-gray-100 text-blue-600 border border-gray-200 hover:bg-gray-200">
                                                    Live Link
                                                </a>
                                            )}
                                            {t.status === 'completed' && t.generated_content && (
                                                <button onClick={() => setCompareTask(t)} className="px-3 py-1 flex items-center justify-center rounded-md transition-colors text-xs font-semibold gap-1 bg-purple-100 text-purple-700 border border-purple-200 hover:bg-purple-200">
                                                    Compare
                                                </button>
                                            )}
                                            {t.error_message && (
                                                <span className="error-text text-red-500 text-xs" title={t.error_message}>Error (Hover)</span>
                                            )}
                                            {['pending', 'completed', 'failed'].includes(t.status) && (
                                                <button onClick={() => handleForceRun(t.id)} title="Run immediately bypassing queue" className="px-3 py-1 flex items-center justify-center rounded-md transition-colors text-xs font-semibold gap-1 bg-orange-50 text-orange-700 border border-orange-200 hover:bg-orange-100">
                                                    Rewrite Now
                                                </button>
                                            )}
                                            {!['pending', 'completed', 'failed'].includes(t.status) && !t.error_message && (
                                                <span className="text-gray-500 text-xs">-</span>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {paginatedTasks.length === 0 && (
                                <tr>
                                    <td colSpan="5" className="text-center py-6 text-gray-500 italic bg-gray-50 font-medium">No tasks in this list.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                )}
                
                {hasMore && (
                    <div className="flex justify-center mt-6 mb-2">
                        <button 
                            onClick={() => setDisplayLimit(prev => prev + 20)}
                            className="btn bg-white hover:bg-gray-50 text-indigo-600 font-bold py-2 px-8 rounded-full border border-indigo-200 shadow-sm transition-all"
                        >
                            Показать еще (Load More)
                        </button>
                    </div>
                )}
            </div>
            
            {/* AI Generated Content Compare Modal */}
            <TaskCompareModal 
                isOpen={!!compareTask} 
                task={compareTask} 
                onClose={() => setCompareTask(null)} 
            />
        </div>
    );
};

export default OrganicSeoTasksManager;
